import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Flame, Target, Utensils, PenLine, Sparkles, Zap } from "lucide-react";
import { SundayCheckinCard } from "@/components/SundayCheckinCard";
import { PromiseTracker } from "@/components/PromiseTracker";
import { HabitsCard } from "@/components/HabitsCard";
import { CoachNudgeCard } from "@/components/CoachNudgeCard";
import { MembershipCta } from "@/components/MembershipCta";
import { ShopCard } from "@/components/ShopCard";

const HERO = "https://images.unsplash.com/photo-1548690312-e3b507d8c110?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxzdHJlZXR3ZWFyJTIwZml0bmVzcyUyMHdvbWFufGVufDB8fHx8MTc4OTQwNDUyNXww&ixlib=rb-4.1.0&q=85";

const COMMITMENTS = [
  { key: "move_done", icon: Flame, label: "Move with intention" },
  { key: "goal_done", icon: Target, label: "Hit your movement goal" },
  { key: "fuel_done", icon: Utensils, label: "Fuel the rebuild" },
  { key: "proof_done", icon: PenLine, label: "Record the proof" },
];

export default function HomePage() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [today, setToday] = useState(null);
  const [reflection, setReflection] = useState("");
  const [saving, setSaving] = useState(false);
  const [achievements, setAchievements] = useState(null);
  const [celebrate, setCelebrate] = useState([]);

  useEffect(() => {
    (async () => {
      const ov = (await api.get("/program/overview")).data;
      setOverview(ov);
      const day = (await api.get(`/program/day/${ov.current_day}`)).data;
      setToday(day);
      setReflection(day.progress?.reflection || "");
      const ach = (await api.get("/progress/achievements")).data;
      setAchievements(ach);
    })();
  }, []);

  const refreshAch = async () => {
    const ach = (await api.get("/progress/achievements")).data;
    setAchievements(ach);
    if (ach.newly_earned?.length) {
      const names = ach.badges.filter((b) => ach.newly_earned.includes(b.id));
      setCelebrate(names);
      setTimeout(() => setCelebrate([]), 6000);
    }
  };

  const toggle = async (key) => {
    if (!today) return;
    const val = !today.progress?.[key];
    const { data } = await api.post("/program/progress", {
      day: today.day.day,
      [key]: val,
    });
    setToday({ ...today, progress: data });
    const ov = (await api.get("/program/overview")).data;
    setOverview(ov);
    await refreshAch();
  };

  const saveReflection = async () => {
    if (!today) return;
    setSaving(true);
    await api.post("/program/progress", {
      day: today.day.day,
      reflection,
      proof_done: reflection.trim().length > 0,
    });
    await api.post("/journal", {
      content: reflection,
      prompt: today.day.prompt,
      day: today.day.day,
    });
    const [ov, day] = await Promise.all([
      api.get("/program/overview"),
      api.get(`/program/day/${today.day.day}`),
    ]);
    setOverview(ov.data);
    setToday(day.data);
    setSaving(false);
    await refreshAch();
  };

  if (!overview || !today) return <div className="p-10 text-zinc-500">Loading…</div>;

  const p = today.progress || {};
  const completedCount = COMMITMENTS.filter((c) => p[c.key]).length;

  return (
    <div className="fade-up">
      {/* Badge unlock toast */}
      {celebrate.length > 0 && (
        <div className="fixed top-20 right-4 z-50 space-y-2" data-testid="badge-toast">
          {celebrate.map((b) => (
            <div key={b.id} className="card fade-up flex items-center gap-3 min-w-[260px]" style={{ borderColor: "#FF0055", background: "#0A0A0A" }}>
              <div className="w-9 h-9 flex items-center justify-center" style={{ background: "#FF0055" }}>
                <Sparkles size={16} color="white"/>
              </div>
              <div>
                <p className="eyebrow" style={{ color: "#FF0055" }}>Badge unlocked</p>
                <p className="font-display font-bold text-sm uppercase mt-0.5">{b.name}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Hero */}
      <section
        className="relative overflow-hidden mb-8 concrete"
        style={{
          minHeight: 260,
          border: "1px solid #27272A",
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.9) 100%), url(${HERO})`,
          backgroundSize: "cover",
          backgroundPosition: "center 30%",
        }}
        data-testid="home-hero"
      >
        <div className="relative z-10 p-6 md:p-10">
          <p className="eyebrow mb-3" style={{ color: "#FF0055" }}>
            Day {overview.current_day} / 30 · Rebuild
          </p>
          <h1 className="font-display font-black text-3xl md:text-5xl leading-[0.95] mb-3">
            HELLO {(user?.name || "REBUILDER").split(" ")[0].toUpperCase()}.
          </h1>
          <p className="text-zinc-300 max-w-lg text-sm md:text-base">
            {today.day.focus === "MOVE" && "Today is a MOVE day. Keep the promise."}
            {today.day.focus === "FUEL" && "Today is a FUEL day. Small, sustainable choices."}
            {today.day.focus === "MIND" && "Today is a MIND day. Confidence is practised."}
            {today.day.focus === "RECOVER" && "Today is a RECOVER day. Rest is training."}
          </p>
          <div className="mt-6 flex gap-3 flex-wrap">
            <Link to="/program" className="btn-primary" data-testid="hero-open-day">
              Open today
            </Link>
            <Link to="/progress" className="btn-secondary" data-testid="hero-coach">
              <span className="inline-flex items-center gap-2"><Sparkles size={14}/> Coach</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8 stagger">
        <div className="card">
          <p className="eyebrow">Current day</p>
          <p className="font-display font-black text-3xl md:text-4xl mt-2">{overview.current_day}<span className="text-zinc-600 text-xl">/30</span></p>
        </div>
        <div className="card" data-testid="streak-card" style={achievements?.current_streak > 0 ? { borderColor: "#FF0055" } : {}}>
          <p className="eyebrow flex items-center gap-1.5" style={achievements?.current_streak > 0 ? { color: "#FF0055" } : {}}>
            <Zap size={11} strokeWidth={2.5}/> Streak
          </p>
          <p className="font-display font-black text-3xl md:text-4xl mt-2" style={{ color: achievements?.current_streak > 0 ? "#FF0055" : "white" }}>
            {achievements?.current_streak ?? 0}
            <span className="text-zinc-600 text-sm ml-2">days</span>
          </p>
        </div>
        <div className="card">
          <p className="eyebrow">Days built</p>
          <p className="font-display font-black text-3xl md:text-4xl mt-2">
            {overview.completed_days}
          </p>
        </div>
        <div className="card">
          <p className="eyebrow">Progress</p>
          <p className="font-display font-black text-3xl md:text-4xl mt-2">{overview.percent}%</p>
        </div>
      </section>

      <CoachNudgeCard />
      <MembershipCta />
      <SundayCheckinCard />
      <PromiseTracker onTick={refreshAch} />

      {/* Today's 4 commitments */}
      <section className="mb-10">
        <div className="flex items-baseline justify-between mb-5">
          <div>
            <p className="eyebrow">Today's proof</p>
            <h2 className="font-display font-black text-2xl md:text-3xl mt-1">
              4 COMMITMENTS <span style={{ color: "#FF0055" }}>({completedCount}/4)</span>
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 stagger">
          {COMMITMENTS.map((c) => {
            const done = !!p[c.key];
            const Icon = c.icon;
            return (
              <button
                key={c.key}
                onClick={() => toggle(c.key)}
                data-testid={`commit-${c.key}`}
                className="card card-interactive text-left flex items-center gap-4"
                style={done ? { borderColor: "#FF0055" } : {}}
              >
                <div
                  className="w-11 h-11 flex items-center justify-center shrink-0"
                  style={{
                    background: done ? "#FF0055" : "#1A1A1A",
                    border: `1px solid ${done ? "#FF0055" : "#3f3f46"}`,
                  }}
                >
                  {done ? <Check size={20} strokeWidth={3} color="white" /> : <Icon size={20} color="#a1a1aa" />}
                </div>
                <div className="flex-1">
                  <p className="eyebrow" style={{ color: done ? "#FF0055" : undefined }}>
                    {done ? "Kept" : "Keep it"}
                  </p>
                  <p className="font-display font-bold text-base md:text-lg mt-1 uppercase">{c.label}</p>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <HabitsCard onTick={refreshAch} />

      {/* Today's focus */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10 stagger">
        <div className="card">
          <p className="eyebrow">Move</p>
          <p className="font-display font-bold mt-2 uppercase">{today.workout?.title}</p>
          <p className="text-sm text-zinc-400 mt-1">{today.workout?.focus} · {today.workout?.duration_min}m</p>
          <Link to={`/workout/${today.workout?.id}`} className="btn-ghost inline-block mt-4 pl-0" data-testid="home-open-workout">Open →</Link>
        </div>
        <div className="card">
          <p className="eyebrow">Fuel focus</p>
          <p className="text-sm text-zinc-200 mt-2 leading-relaxed">{today.day.fuel_focus}</p>
          <Link to="/fuel" className="btn-ghost inline-block mt-4 pl-0">Log a meal →</Link>
        </div>
        <div className="card">
          <p className="eyebrow">Recovery</p>
          <p className="text-sm text-zinc-200 mt-2 leading-relaxed">{today.day.recovery}</p>
        </div>
      </section>

      {/* Record the proof */}
      <section className="card mb-10" data-testid="record-proof-card">
        <p className="eyebrow" style={{ color: "#FF0055" }}>Record the proof</p>
        <p className="font-display font-black text-xl md:text-2xl mt-2 uppercase leading-tight">
          {today.day.prompt}
        </p>
        <textarea
          className="input mt-4"
          rows={4}
          placeholder="Write your proof for today…"
          value={reflection}
          onChange={(e) => setReflection(e.target.value)}
          data-testid="reflection-input"
        />
        <div className="mt-4 flex gap-3">
          <button className="btn-primary" onClick={saveReflection} disabled={saving || !reflection.trim()} data-testid="save-reflection">
            {saving ? "Saving…" : "Save proof"}
          </button>
          <Link to="/journal" className="btn-secondary">
            View journal
          </Link>
        </div>
      </section>

      <ShopCard />
    </div>
  );
}
